import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, asArray, formatApiError } from "@/lib/api";
import { SeverityBadge } from "@/components/Primitives";
import { Repeat, Hammer } from "lucide-react";
import { toast } from "sonner";

const DIMS = ["", "asset", "user", "client", "category"];

export default function RecurringV2() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [dim, setDim] = useState("");
  const [counts, setCounts] = useState(null);

  // The single-dimension (asset+category) detector, kept separate from the
  // cross-dimension one below: convert-to-problem reads db.recurring_issues by
  // id, and this detector -- not recurring/v2's own rv2_-prefixed collection
  // -- is what writes there. Wiring Convert to the wrong collection's ids
  // would 404 every time it was pressed.
  const [v1Items, setV1Items] = useState([]);
  const [convertingId, setConvertingId] = useState(null);

  const load = (d) => api.get("/recurring/v2", { params: d ? { dimension: d } : {} }).then(r => setItems(asArray(r.data)));
  useEffect(() => { load(dim); }, [dim]);

  const loadV1 = () => api.get("/recurring-issues").then(r => setV1Items(asArray(r.data))).catch(() => setV1Items([]));
  useEffect(() => { loadV1(); }, []);

  const detect = async () => {
    const { data } = await api.post("/recurring/detect-v2");
    setCounts(data.by_dimension);
    toast.success(`Detected ${data.detected} cross-dimension patterns`);
    load(dim);
  };

  const detectV1 = async () => {
    const { data } = await api.post("/recurring-issues/detect");
    toast.success(`Detected ${data.detected} recurring issues`);
    loadV1();
  };

  const convert = async (rid) => {
    setConvertingId(rid);
    try {
      const { data } = await api.post(`/recurring-issues/${rid}/convert-to-problem`);
      nav(`/problems/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConvertingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Recurring Issue Intelligence</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Recurring Issues</h1>
          <p className="text-sm text-zinc-500 mt-1">Single-issue detector (asset + category, 3+ tickets). These are the only recurring issues that can become a Problem Record.</p>
        </div>
        <button onClick={detectV1} data-testid="rv1-detect-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2"><Repeat size={14}/> Run detection</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {v1Items.map(r => (
          <div key={r.id} className="bg-zinc-950 border border-zinc-900 rounded-md p-4" data-testid={`rv1-row-${r.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <SeverityBadge level={r.severity}/>
                <div className="text-xs uppercase tracking-widest font-mono text-zinc-500">{r.category}</div>
              </div>
              {r.status === "converted" && r.problem_id ? (
                <button onClick={() => nav(`/problems/${r.problem_id}`)} data-testid={`rv1-view-problem-${r.id}`}
                  className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                  <Hammer size={12} /> View problem
                </button>
              ) : (
                <button onClick={() => convert(r.id)} disabled={convertingId === r.id} data-testid={`rv1-convert-${r.id}`}
                  className="text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-2.5 py-1 flex items-center gap-1">
                  <Hammer size={12} /> {convertingId === r.id ? "Converting…" : "Convert to Problem"}
                </button>
              )}
            </div>
            <div className="text-[11px] text-zinc-500 font-mono mt-2">
              {r.occurrences} occurrences · {r.first_seen?.slice(0,10)} → {r.last_seen?.slice(0,10)}
            </div>
            {r.suggested_root_cause && <div className="text-sm text-zinc-300 mt-2">{r.suggested_root_cause}</div>}
          </div>
        ))}
        {v1Items.length === 0 && <div className="text-zinc-500 text-sm">Run detection to surface recurring issues.</div>}
      </div>

      <div className="flex items-end justify-between border-t border-zinc-900 pt-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Recurring Issue Intelligence 2.0</div>
          <h2 className="font-display text-2xl font-semibold tracking-tight mt-1">Cross-Dimension Patterns</h2>
          <p className="text-sm text-zinc-500 mt-1">Cross-dimension detector (asset, user, client, or category, 4+ tickets), informational only — nothing here converts to a Problem Record.</p>
        </div>
        <button onClick={detect} data-testid="rv2-detect-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2"><Repeat size={14}/> Run detection</button>
      </div>

      {counts && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {Object.entries(counts).map(([k,v]) => (
            <div key={k} className="bg-zinc-950 border border-zinc-900 rounded px-3 py-2 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{k}</div>
              <div className="font-mono">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        {DIMS.map(d => (
          <button key={d || "all"} onClick={()=>setDim(d)} data-testid={`rv2-filter-${d || "all"}`} className={`text-xs rounded px-3 py-1.5 border ${dim===d?"bg-blue-600 text-white border-blue-600":"bg-zinc-950 border-zinc-800 text-zinc-300"}`}>{d || "all"}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {items.map(r => (
          <div key={r.id} className="bg-zinc-950 border border-zinc-900 rounded-md p-4">
            <div className="flex items-center gap-2">
              <SeverityBadge level={r.severity}/>
              <div className="text-xs uppercase tracking-widest font-mono text-zinc-500">{r.dimension}</div>
            </div>
            <div className="text-sm mt-2 font-mono text-zinc-200">{r.key}</div>
            <div className="text-[11px] text-zinc-500 font-mono mt-1">{r.occurrences} occurrences · {r.first_seen?.slice(0,10)} → {r.last_seen?.slice(0,10)}</div>
          </div>
        ))}
        {items.length === 0 && <div className="text-zinc-500 text-sm">Run detection to surface patterns.</div>}
      </div>
    </div>
  );
}
